import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { markWelcomeSeen } from "../lib/firstVisit";
import { useI18n } from "../lib/i18n";
import type { MessageKey } from "../lib/messages";
import { IconChat, IconSearch, IconWhatsApp } from "../ui/icons";
import { Grade, VerdictTag } from "../ui/kit";

// The welcome page: first-time visitors land here once (see lib/firstVisit). The promo reel beside a search
// box you can actually use, then each promise shown with the piece of the store that keeps it.

const SAMPLES: MessageKey[] = ["welcome.sample1", "welcome.sample2", "welcome.sample3"];
const reducedMotion = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Types sample searches into the placeholder, one after another, the way the video does. */
function useTypedPlaceholder(samples: string[]) {
  const [text, setText] = useState(samples[0] ?? "");
  useEffect(() => {
    if (reducedMotion() || samples.length === 0) return setText(samples[0] ?? "");
    let i = 0;
    let n = 0;
    let deleting = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      const s = samples[i]!;
      n += deleting ? -1 : 1;
      setText(s.slice(0, n));
      let wait = deleting ? 28 : 70;
      if (!deleting && n >= s.length) [deleting, wait] = [true, 1800];
      else if (deleting && n <= 0) [deleting, i, wait] = [false, (i + 1) % samples.length, 350];
      timer = setTimeout(tick, wait);
    };
    timer = setTimeout(tick, 600);
    return () => clearTimeout(timer);
  }, [samples.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
  return text;
}

export function Welcome() {
  const { t, money } = useI18n();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const samples = SAMPLES.map((k) => t(k));
  const typed = useTypedPlaceholder(samples);
  useEffect(() => {
    document.title = `Walashy · ${t("welcome.title")}`;
  }, [t]);
  useEffect(markWelcomeSeen, []);

  const search = (e: FormEvent) => {
    e.preventDefault();
    const query = q.trim() || samples[0]!;
    nav(`/?q=${encodeURIComponent(query)}`);
  };

  return (
    <div className="welcome">
      <section className="welcome-hero" aria-labelledby="welcome-title">
        <div className="welcome-hero-text">
          <h1 id="welcome-title">{t("welcome.title")}</h1>
          <p className="welcome-lede">{t("welcome.lede")}</p>
          <form className="welcome-search" role="search" onSubmit={search}>
            <label htmlFor="welcome-q" className="sr-only">{t("browse.searchLabel")}</label>
            <input id="welcome-q" className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={typed} dir="auto" autoComplete="off" />
            <button className="welcome-search-btn" aria-label={t("browse.search")}><IconSearch size={20} /></button>
          </form>
          <p className="welcome-hint">{t("welcome.hint")}</p>
          <div className="row wrap welcome-ctas">
            <Link className="btn primary" to="/">{t("welcome.browse")}</Link>
            <Link className="btn" to="/sell">{t("welcome.sell")}</Link>
          </div>
        </div>
        <PromoVideo label={t("welcome.videoLabel")} />
      </section>

      <section className="welcome-promises" aria-label={t("welcome.promisesLabel")}>
        <PromiseRow title={t("welcome.p.price.title")} body={t("welcome.p.price.body")}>
          <div className="wp-price">
            <span className="price num">{money(149900, "SAR")}</span>
            <VerdictTag v="great" />
          </div>
        </PromiseRow>
        <PromiseRow title={t("welcome.p.grade.title")} body={t("welcome.p.grade.body")}>
          <div className="wp-grades">
            {(["new", "like_new", "good", "fair"] as const).map((c) => (
              <span key={c} className="wp-grade"><Grade c={c} /> {t(`cond.${c}`)}</span>
            ))}
          </div>
        </PromiseRow>
        <PromiseRow title={t("welcome.p.privacy.title")} body={t("welcome.p.privacy.body")}>
          <div className="wp-chat">
            <span className="wp-bubble" dir="auto">{t("welcome.p.privacy.message")}</span>
            <span className="row" style={{ gap: 6 }}>
              <span className="tag"><IconChat size={14} /> {t("contactPrefs.chat")}</span>
              <span className="tag good"><IconWhatsApp size={14} /> {t("contact.whatsapp")}</span>
            </span>
            <span className="small muted">{t("welcome.p.privacy.note")}</span>
          </div>
        </PromiseRow>
        <PromiseRow title={t("welcome.p.pay.title")} body={t("welcome.p.pay.body")}>
          <ol className="wp-flow" aria-label={t("welcome.p.pay.title")}>
            <li>{t("welcome.p.pay.s1")}</li>
            <li>{t("welcome.p.pay.s2")}</li>
            <li>{t("welcome.p.pay.s3")}</li>
            <li>{t("welcome.p.pay.s4")}</li>
          </ol>
        </PromiseRow>
      </section>

      <section className="welcome-sell" aria-labelledby="welcome-sell-title">
        <h2 id="welcome-sell-title">{t("welcome.sellTitle")}</h2>
        <ol className="welcome-steps">
          <li><strong>{t("welcome.step1.title")}</strong><span>{t("welcome.step1.body")}</span></li>
          <li><strong>{t("welcome.step2.title")}</strong><span>{t("welcome.step2.body")}</span></li>
          <li><strong>{t("welcome.step3.title")}</strong><span>{t("welcome.step3.body")}</span></li>
        </ol>
        <Link className="btn primary" to="/sell/new">{t("welcome.sellCta")}</Link>
      </section>

      <section className="welcome-end">
        <p className="welcome-wordmark" aria-hidden>Walashy</p>
        <p className="welcome-end-line">{t("welcome.closing")}</p>
        <Link className="btn primary" to="/">{t("welcome.browse")}</Link>
      </section>
    </div>
  );
}

function PromiseRow({ title, body, children }: { title: string; body: string; children: ReactNode }) {
  return (
    <div className="welcome-promise">
      <div className="welcome-promise-text">
        <h2>{title}</h2>
        <p>{body}</p>
      </div>
      <div className="welcome-promise-proof" aria-hidden>{children}</div>
    </div>
  );
}

/** The promo reel: plays muted on loop (still for people who turn motion off); sound and pause one tap away. */
function PromoVideo({ label }: { label: string }) {
  const { t } = useI18n();
  const ref = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const still = reducedMotion();
  useEffect(() => {
    if (!still) ref.current?.play().catch(() => setPlaying(false));
  }, [still]);
  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) void v.play().catch(() => {});
    else v.pause();
  };
  return (
    <figure className="welcome-video">
      <div className="welcome-video-frame">
        <video
          ref={ref}
          src="/promo/walashy.mp4"
          poster="/promo/walashy-poster.jpg"
          muted={muted}
          loop
          playsInline
          preload={still ? "none" : "auto"}
          aria-label={label}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onClick={toggle}
        />
      </div>
      <figcaption className="welcome-video-controls">
        <button type="button" className="btn sm" onClick={toggle}>{playing ? t("welcome.pause") : t("welcome.play")}</button>
        <button type="button" className="btn sm ghost" onClick={() => setMuted((m) => !m)} aria-pressed={!muted}>
          {muted ? t("welcome.soundOn") : t("welcome.soundOff")}
        </button>
      </figcaption>
    </figure>
  );
}
